import { ChangeDetectionStrategy, Component, effect, inject, input, output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { SearchableLookupSelectComponent } from '@shared/components/searchable-lookup-select/searchable-lookup-select.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { LookupResponse } from '@core/models/common.model';
import { ClientCompanyResponse, ClientResponse } from '@features/client/client.model';
import {
  CompanyNamedLookupResponse,
  CompanyOrganisationalLevelResponse,
  CompanyResponse,
} from '@features/company/company.model';
import {
  CreateEmployeeRequest,
  EmployeeLookupResponse,
  EmployeeResponse,
} from '../../employee.model';
import { toNullableDateString } from '@shared/utils/date-utils';
import { nullIfBlank } from '@shared/utils/payload-utils';

const EMPLOYEE_FORM_DRAFT_KEY = 'employee-form-draft-v1';
const EMPLOYEE_FORM_DATE_FIELDS = ['dateOfEmployment', 'extractDate', 'dateOfBirth'] as const;
const EMPLOYEE_FORM_STEP_FIELDS = {
  company: [
    'clientId', 'companyId', 'clientCompanyId', 'employeeStatusId', 'companyDivisionId', 'companyJobTitleId',
    'classification', 'managerEmployeeId', 'extractDate',
    'currentLevel1Id', 'currentLevel2Id', 'currentLevel3Id', 'currentLevel4Id', 'currentLevel5Id', 'currentLevel6Id',
  ],
  personal: [
    'firstName', 'surname', 'emailAddress', 'contactNumber', 'employeeNumber', 'employeeHeadCount',
    'titleId', 'genderId', 'raceId', 'maritalStatusId', 'medicalAidId', 'dateOfBirth',
    'countryId', 'provinceId', 'location', 'employmentTypeId', 'dateOfEmployment', 'groupProductId',
    'idNumber', 'nationalIdNumber', 'passportNumber',
  ],
} as const;

@Component({
  selector: 'employee-form',
  imports: [ReactiveFormsModule, SearchableLookupSelectComponent, ...SharedMaterialUIModule],
  templateUrl: './employee-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EmployeeFormComponent {
  private readonly formBuilder = inject(FormBuilder);
  private draftRestored = false;

  readonly title = input('Employee form');
  readonly submitLabel = input('Save');
  readonly busy = input(false);
  readonly readOnly = input(false);
  readonly initialValue = input<EmployeeResponse | null>(null);

  readonly clients = input<ClientResponse[]>([]);
  readonly clientCompanies = input<ClientCompanyResponse[]>([]);
  readonly countries = input<LookupResponse[]>([]);
  readonly employmentTypes = input<LookupResponse[]>([]);
  readonly genders = input<LookupResponse[]>([]);
  readonly groupProducts = input<LookupResponse[]>([]);
  readonly maritalStatuses = input<LookupResponse[]>([]);
  readonly medicalAids = input<LookupResponse[]>([]);
  readonly provinces = input<LookupResponse[]>([]);
  readonly races = input<LookupResponse[]>([]);
  readonly titles = input<LookupResponse[]>([]);
  readonly employeeStatuses = input<LookupResponse[]>([]);
  readonly companies = input<CompanyResponse[]>([]);
  readonly companyDivisions = input<CompanyNamedLookupResponse[]>([]);
  readonly companyJobTitles = input<CompanyNamedLookupResponse[]>([]);
  readonly organisationalLevels = input<CompanyOrganisationalLevelResponse[]>([]);
  readonly managerLookupResponses = input<EmployeeLookupResponse[]>([]);
  readonly managerLookupLoading = input(false);

  readonly submitted = output<CreateEmployeeRequest>();
  readonly clientChanged = output<string | null>();
  readonly companyChanged = output<string | null>();
  readonly managerLookupSearchChanged = output<string>();

  readonly employeeForm = this.formBuilder.group({
    clientId: ['', Validators.required],
    clientCompanyId: [{ value: '', disabled: true }, Validators.required],
    companyId: ['', Validators.required],
    classification: ['', Validators.required],
    groupProductId: ['', Validators.required],
    titleId: ['', Validators.required],
    firstName: ['', Validators.required],
    surname: ['', Validators.required],
    employeeNumber: ['', Validators.required],
    idNumber: ['', Validators.required],
    nationalIdNumber: [''],
    passportNumber: [''],
    countryId: [''],
    raceId: [''],
    genderId: [''],
    maritalStatusId: [''],
    medicalAidId: [''],
    dateOfEmployment: ['', Validators.required],
    extractDate: ['', Validators.required],
    dateOfBirth: ['', Validators.required],
    employmentTypeId: ['', Validators.required],
    companyJobTitleId: ['', Validators.required],
    contactNumber: ['', Validators.required],
    emailAddress: ['', [Validators.required, Validators.email]],
    managerEmployeeId: [''],
    provinceId: ['', Validators.required],
    location: [''],
    currentLevel1Id: ['', Validators.required],
    currentLevel2Id: ['', Validators.required],
    currentLevel3Id: [''],
    currentLevel4Id: [''],
    currentLevel5Id: [''],
    currentLevel6Id: [''],
    employeeHeadCount: [''],
    companyDivisionId: [''],
    employeeStatusId: ['', Validators.required],
  });

  constructor() {
    // When editing an existing employee, hydrate the form from initialValue and clear any create-form draft.
    effect(() => {
      const employee = this.initialValue();
      if (!employee) {
        return;
      }

      this.employeeForm.reset({
        clientId: employee.clientId,
        clientCompanyId: employee.clientCompanyId,
        companyId: employee.companyId,
        classification: employee.classification ?? '',
        groupProductId: employee.groupProductId ?? '',
        titleId: employee.titleId ?? '',
        firstName: employee.firstName,
        surname: employee.surname,
        employeeNumber: employee.employeeNumber ?? '',
        idNumber: employee.idNumber ?? '',
        nationalIdNumber: employee.nationalIdNumber ?? '',
        passportNumber: employee.passportNumber ?? '',
        countryId: employee.countryId ?? '',
        raceId: employee.raceId ?? '',
        genderId: employee.genderId ?? '',
        maritalStatusId: employee.maritalStatusId ?? '',
        medicalAidId: employee.medicalAidId ?? '',
        dateOfEmployment: employee.dateOfEmployment ?? '',
        extractDate: employee.extractDate ?? '',
        dateOfBirth: employee.dateOfBirth ?? '',
        employmentTypeId: employee.employmentTypeId ?? '',
        companyJobTitleId: employee.companyJobTitleId ?? '',
        contactNumber: employee.contactNumber ?? '',
        emailAddress: employee.emailAddress ?? '',
        managerEmployeeId: employee.managerEmployeeId ?? '',
        provinceId: employee.provinceId ?? '',
        location: employee.location ?? '',
        currentLevel1Id: employee.currentLevel1Id ?? '',
        currentLevel2Id: employee.currentLevel2Id ?? '',
        currentLevel3Id: employee.currentLevel3Id ?? '',
        currentLevel4Id: employee.currentLevel4Id ?? '',
        currentLevel5Id: employee.currentLevel5Id ?? '',
        currentLevel6Id: employee.currentLevel6Id ?? '',
        employeeHeadCount: employee.employeeHeadCount?.toString() ?? '',
        companyDivisionId: employee.companyDivisionId ?? '',
        employeeStatusId: employee.employeeStatusId ?? '',
      });

      this.clearDraft();
    });

    // In detail mode, infer and set clientId from the selected client-company mapping when available.
    effect(() => {
      const employee = this.initialValue();
      if (!employee) {
        return;
      }

      const selectedClientCompany = this.clientCompanies().find((item) => item.id === employee.clientCompanyId);
      if (!selectedClientCompany) {
        return;
      }

      this.employeeForm.patchValue(
        {
          clientId: selectedClientCompany.clientId,
        },
        { emitEvent: false },
      );
    });

    // On create mode only, restore a previously saved draft once when the form initializes.
    effect(() => {
      const employee = this.initialValue();
      if (employee || this.readOnly() || this.draftRestored) {
        return;
      }

      const draft = this.loadDraft();
      if (!draft) {
        this.draftRestored = true;
        return;
      }

      this.employeeForm.patchValue(draft, { emitEvent: false });
      this.draftRestored = true;
    });

    // Persist create-form changes so test data survives refresh/navigation while filling out the form.
    this.employeeForm.valueChanges.subscribe(() => {
      if (this.initialValue() || this.readOnly() || !this.draftRestored) {
        return;
      }

      this.saveDraft();
    });

    this.employeeForm.controls.clientId.valueChanges.subscribe(() => {
      this.updateClientCompanyControlState();
    });

    this.employeeForm.controls.companyId.valueChanges.subscribe(() => {
      this.updateClientCompanyControlState();
    });

    this.updateClientCompanyControlState();
  }

  protected onClientChanged(): void {
    const clientIdControl = this.employeeForm.get('clientId');
    const clientId = clientIdControl?.value as string | null;
    this.clientChanged.emit(clientId?.trim() ? clientId : null);
    // Reset company and unique company code selections when client changes.
    this.employeeForm.get('companyId')?.reset('');
    this.employeeForm.get('clientCompanyId')?.reset('');
    this.companyChanged.emit(null);
  }

  protected onCompanyChanged(): void {
    const companyIdControl = this.employeeForm.get('companyId');
    const companyId = companyIdControl?.value as string | null;
    // Unique company code is tied to the selected client+company pair.
    this.employeeForm.get('clientCompanyId')?.reset('');
    this.companyChanged.emit(companyId?.trim() ? companyId : null);
  }

  protected onManagerLookupSearchChanged(value: string): void {
    this.managerLookupSearchChanged.emit(value.trim());
  }

  protected reset(): void {
    this.employeeForm.reset();
    this.clearDraft();
  }

  protected hasError(controlName: string, errorKey: string): boolean {
    const control = this.employeeForm.get(controlName);
    return !!control && control.touched && control.hasError(errorKey);
  }

  protected stepHasError(step: keyof typeof EMPLOYEE_FORM_STEP_FIELDS): boolean {
    return EMPLOYEE_FORM_STEP_FIELDS[step].some((controlName) => {
      const control = this.employeeForm.get(controlName);
      return !!control && control.touched && control.invalid;
    });
  }

  private saveDraft(): void {
    const raw = this.employeeForm.getRawValue();
    localStorage.setItem(EMPLOYEE_FORM_DRAFT_KEY, JSON.stringify(raw));
  }

  private loadDraft(): Record<string, unknown> | null {
    const raw = localStorage.getItem(EMPLOYEE_FORM_DRAFT_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      for (const dateField of EMPLOYEE_FORM_DATE_FIELDS) {
        const value = parsed[dateField];
        if (typeof value === 'string' && value.trim().length > 0) {
          const parsedDate = new Date(value);
          if (!Number.isNaN(parsedDate.getTime())) {
            parsed[dateField] = parsedDate;
          }
        }
      }

      return parsed;
    } catch {
      return null;
    }
  }

  private clearDraft(): void {
    localStorage.removeItem(EMPLOYEE_FORM_DRAFT_KEY);
  }

  private updateClientCompanyControlState(): void {
    const hasClient = !!this.employeeForm.controls.clientId.value;
    const hasCompany = !!this.employeeForm.controls.companyId.value;
    const clientCompanyControl = this.employeeForm.controls.clientCompanyId;

    if (hasClient && hasCompany) {
      if (clientCompanyControl.disabled) {
        clientCompanyControl.enable({ emitEvent: false });
      }
      return;
    }

    clientCompanyControl.reset('', { emitEvent: false });
    if (clientCompanyControl.enabled) {
      clientCompanyControl.disable({ emitEvent: false });
    }
  }

  protected submit(): void {
    if (this.readOnly()) {
      return;
    }

    if (this.employeeForm.invalid) {
      this.employeeForm.markAllAsTouched();
      return;
    }

    const value = this.employeeForm.getRawValue();
    const isActive = this.initialValue()?.isActive ?? true;
    this.submitted.emit({
      clientId: required(value.clientId),
      clientCompanyId: required(value.clientCompanyId),
      companyId: required(value.companyId),
      isActive,
      classification: nullIfBlank(value.classification),
      groupProductId: nullIfBlank(value.groupProductId),
      titleId: nullIfBlank(value.titleId),
      firstName: required(value.firstName),
      surname: required(value.surname),
      employeeNumber: nullIfBlank(value.employeeNumber),
      idNumber: nullIfBlank(value.idNumber),
      nationalIdNumber: nullIfBlank(value.nationalIdNumber),
      passportNumber: nullIfBlank(value.passportNumber),
      countryId: nullIfBlank(value.countryId),
      raceId: nullIfBlank(value.raceId),
      genderId: nullIfBlank(value.genderId),
      maritalStatusId: nullIfBlank(value.maritalStatusId),
      medicalAidId: nullIfBlank(value.medicalAidId),
      dateOfEmployment: toNullableDateString(value.dateOfEmployment),
      extractDate: toNullableDateString(value.extractDate),
      dateOfBirth: toNullableDateString(value.dateOfBirth),
      employmentTypeId: nullIfBlank(value.employmentTypeId),
      companyJobTitleId: nullIfBlank(value.companyJobTitleId),
      contactNumber: nullIfBlank(value.contactNumber),
      emailAddress: nullIfBlank(value.emailAddress),
      managerEmployeeId: nullIfBlank(value.managerEmployeeId),
      provinceId: nullIfBlank(value.provinceId),
      location: nullIfBlank(value.location),
      currentLevel1Id: nullIfBlank(value.currentLevel1Id),
      currentLevel2Id: nullIfBlank(value.currentLevel2Id),
      currentLevel3Id: nullIfBlank(value.currentLevel3Id),
      currentLevel4Id: nullIfBlank(value.currentLevel4Id),
      currentLevel5Id: nullIfBlank(value.currentLevel5Id),
      currentLevel6Id: nullIfBlank(value.currentLevel6Id),
      employeeHeadCount: toNullableNumber(value.employeeHeadCount),
      companyDivisionId: nullIfBlank(value.companyDivisionId),
      employeeStatusId: nullIfBlank(value.employeeStatusId)
    });
  }
}


function required(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function toNullableNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
